/**
 * INSPO PAGE (web v1.1)
 *
 * Read + delete surface for a user's inspo items. Add / edit stay on
 * mobile in v1.1 — the mobile workflow (screenshot → tag polishes →
 * save) is the primary path. Web is here so users can prune inspo from
 * either device (which is what unblocked this ship — a user deleted a
 * Creation on web that shared a photo with an Inspo doc and expected
 * the Inspo to be gone too; without a web surface there was no way to
 * remove the Inspo from anything other than the phone).
 */

import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { Link } from 'react-router-dom';
import { Sparkles, Search, Trash2, Smartphone, Link as LinkIcon, Palette } from 'lucide-react';
import { auth } from '../firebase/config';
import { PageHeader } from '../components/common';
import { getUserInspo, deleteInspo } from '../services/inspoService';
import type { Inspo, InspoStatus } from '../types/inspo';

type StatusFilter = 'all' | InspoStatus;

const InspoPage: React.FC = () => {
  const [inspo, setInspo] = useState<Inspo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setInspo([]);
        setLoading(false);
        setCurrentUserId(null);
        return;
      }
      setCurrentUserId(user.uid);
      setLoading(true);
      try {
        setInspo(await getUserInspo(user.uid));
      } catch (err) {
        console.error('Error loading inspo', err);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleDelete = async (item: Inspo) => {
    if (!currentUserId) return;
    if (!confirm(`Delete this inspo? This can't be undone.`)) return;
    setDeletingId(item.id);
    try {
      await deleteInspo(currentUserId, item);
      setInspo((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err) {
      console.error('Error deleting inspo', err);
      alert('Could not delete this inspo. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const q = searchQuery.toLowerCase();
  const filtered = inspo.filter((item) => {
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    if (!matchesStatus) return false;
    if (!q) return true;
    if (item.caption?.toLowerCase().includes(q)) return true;
    if (item.customTags?.some((t) => t.toLowerCase().includes(q))) return true;
    if (item.artTypes?.some((t) => t.toLowerCase().includes(q))) return true;
    if (item.linkedItems?.some((i) => i.name.toLowerCase().includes(q))) return true;
    if (item.linkedPolishSlugs?.some((s) => s.toLowerCase().includes(q))) return true;
    return false;
  });

  const totalInspo = inspo.length;
  const wantToTryCount = inspo.filter((i) => i.status === 'want-to-try').length;
  const triedCount = inspo.filter((i) => i.status === 'tried').length;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 border-4 border-slate-600 border-t-slate-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-glass-secondary font-body">Loading your inspo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      <PageHeader
        icon={Sparkles}
        title="My Inspo"
        subtitle="Your saved nail-art inspiration — searchable by linked polishes and tags"
        className="mb-6"
        actions={
          <Link
            to="/creations"
            className="flex items-center gap-2 px-4 py-2 rounded-full font-body text-glass-secondary border border-slate-600 hover:bg-white/10 transition-all"
          >
            <Palette className="icon-md" />
            My Creations
          </Link>
        }
      >
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="glass-stats p-4 text-center border border-amber-200/50">
            <p className="text-3xl font-bold text-amber-600">{totalInspo}</p>
            <p className="text-sm text-amber-500 font-body">Total Inspo</p>
          </div>
          <div className="glass-stats p-4 text-center border border-slate-200/50">
            <p className="text-3xl font-bold text-slate-600">{wantToTryCount}</p>
            <p className="text-sm text-slate-500 font-body">Want to try</p>
          </div>
          <div className="glass-stats p-4 text-center border border-emerald-200/50">
            <p className="text-3xl font-bold text-emerald-600">{triedCount}</p>
            <p className="text-sm text-emerald-500 font-body">Tried</p>
          </div>
        </div>
      </PageHeader>

      {/* Mobile-only hint — Add / Edit stay on mobile in v1.1 */}
      <div className="glass-card p-3 mb-6 flex items-center gap-3">
        <Smartphone className="icon-md text-glass-secondary flex-shrink-0" />
        <p className="text-sm text-glass-secondary font-body">
          Adding and editing inspo is done in the mobile app. This page lets you review and remove
          what you've saved.
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="glass-card p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
              <input
                type="text"
                placeholder="Search caption, tag, linked polish..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-white/80 font-body"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="px-4 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 bg-white/80 font-body"
            >
              <option value="all">All Status</option>
              <option value="want-to-try">Want to try</option>
              <option value="tried">Tried</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <div className="w-24 h-24 glass-stats rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-600/50">
            <Sparkles className="icon-2xl text-glass-secondary" />
          </div>
          <h3 className="text-xl font-bold text-glass-secondary mb-2 font-heading">
            {inspo.length === 0 ? 'No inspo saved yet!' : 'No matching inspo'}
          </h3>
          <p className="text-glass-secondary mb-2 font-body">
            {inspo.length === 0
              ? 'Screenshot a mani you love, then save it from the mobile app.'
              : 'Try adjusting your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((item) => {
            const linkedCount =
              (item.linkedPolishSlugs?.length ?? 0) + (item.linkedItems?.length ?? 0);
            const tried = item.status === 'tried';
            const isDeleting = deletingId === item.id;
            return (
              <div
                key={item.id}
                className="glass-card overflow-hidden flex flex-col"
              >
                <div className="relative aspect-square bg-slate-100">
                  {item.photo?.url ? (
                    <img
                      src={item.photo.url}
                      alt={item.caption || 'inspo'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl">
                      ✨
                    </div>
                  )}
                  <span className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-bold">
                    <Sparkles className="w-3 h-3" />
                    Inspo
                  </span>
                  <span
                    className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                      tried
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-800/80 text-white border border-slate-500'
                    }`}
                  >
                    {tried ? '✓ Tried' : 'Want to try'}
                  </span>
                </div>

                <div className="p-3 flex-1 flex flex-col">
                  {item.caption ? (
                    <p className="text-sm text-glass font-body line-clamp-2 mb-2">
                      {item.caption}
                    </p>
                  ) : (
                    <p className="text-sm text-glass-muted font-body italic mb-2">
                      No caption
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-2 text-xs text-glass-secondary font-body mb-3">
                    {linkedCount > 0 && (
                      <span className="flex items-center gap-1">
                        <LinkIcon className="w-3 h-3" />
                        {linkedCount} linked
                      </span>
                    )}
                    {item.artTypes && item.artTypes.length > 0 && (
                      <span className="truncate">
                        {item.artTypes.slice(0, 2).join(' · ')}
                      </span>
                    )}
                    {item.season && <span>· {item.season}</span>}
                  </div>

                  {item.sourceUrl && (
                    <a
                      href={item.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-purple-600 hover:underline font-body truncate mb-3"
                    >
                      {item.sourceUrl}
                    </a>
                  )}

                  <button
                    onClick={() => handleDelete(item)}
                    disabled={isDeleting}
                    className="mt-auto flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm font-bold font-body border border-red-300 text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    {isDeleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InspoPage;
